import { useState, useRef, useEffect, useMemo } from "react";
import { Button } from "../../../components/Button";
import { api } from "../../../services/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useUser } from "../../../services/user-context";
import { useApp } from "../../../services/app-context";
import "react-datepicker/dist/react-datepicker.css";
import { ShowToast } from "../../../services/toast";
import { CertificationFormBasicFields } from "./CertificationFormBasicFields";
import { CertificationFormQuestions } from "./CertificationFormQuestions";
import { twMerge } from "tailwind-merge";
import { getLabel } from "../utils/labels";
import { buildCallFlowSavePayload } from "../utils/callFlow";
import { buildCertificationMetadataPayload } from "../utils/metadata";
import { getCertificationDueDateValidationError } from "../utils/dates";
import { ReactComponent as ExclamationCircleIcon } from "../../../assets/icons/exclamation-circle.svg";
import { isCertificationOverallSummaryEnabled } from "../../../integrations/posthog/featureFlags";
import SideModal from "./SideModal";
import { CertificationSteps } from "./CertificationSteps";
import { CertificationAssignUserStep } from "./CertificationAssignUserStep";

export const DuplicateCertificationModal = ({
  onClose,
  isOpen,
  onSuccess,
  sourceCertificationId,
}: {
  onClose: () => void;
  isOpen: boolean;
  onSuccess: () => void;
  sourceCertificationId: string;
}) => {
  const { userInfo } = useUser() as any;
  const { tenantProfile, certificationAssets } = useApp();
  const labels = certificationAssets?.labels || {};
  const sessionSingular = getLabel("session.singular", labels);
  const showOverallSummary = isCertificationOverallSummaryEnabled();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const getInitialForm = () => ({
    primer: "",
    isActive: 1,
    certificationAttemptsRequired: 1,
    certificationAttemptsAllowed: 1,
    certificationStartDate: null,
    certificationDueDate: null,
    certificationName: "",
    regionId: "",
    territoryId: "",
    isLocationBased: 0,
    isProductBased: 0,
    scoringMethod: "average",
    salesAidFile: "",
    code: "",
    userList: [],
    callFlow: [{ question: "", category: "" }],
    productName: tenantProfile?.product,
  });

  const [step, setStep] = useState<number>(1);
  const [form, setForm] = useState<any>(getInitialForm);

  // Fetch source metadata for pre-fill
  const { data: sourceDetailsData, isLoading: isLoadingDetails } = useQuery({
    queryKey: ["certification-details", sourceCertificationId],
    queryFn: () => api.getCertificationDetails(sourceCertificationId),
    enabled: isOpen && !!sourceCertificationId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  // Fetch source call flow for pre-fill
  const { data: sourceCallFlowData, isLoading: isLoadingCallFlow } = useQuery({
    queryKey: ["certification-call-flow-details", sourceCertificationId],
    queryFn: () => api.getCertificationCallFlowDetails(sourceCertificationId),
    enabled: isOpen && !!sourceCertificationId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  // Reset form and step each time the modal opens
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setStep(1);
      setForm(getInitialForm());
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  // Pre-fill step-1 fields from source metadata
  useEffect(() => {
    if (!sourceDetailsData?.data) return;
    const d = sourceDetailsData.data;
    setForm((prev: any) => ({
      ...prev,
      certificationName: `${d.certificationName} (Copy)`,
      certificationAttemptsAllowed: d.certificationAttemptsAllowed ?? prev.certificationAttemptsAllowed,
      certificationAttemptsRequired: d.certificationAttemptsRequired ?? prev.certificationAttemptsRequired,
      primer: d.primer ?? prev.primer,
      salesAidFile: d.salesAidFile ? String(d.salesAidFile) : prev.salesAidFile,
      isActive: d.isActive ? 1 : 0,
      scoringMethod: d.scoringMethod ?? prev.scoringMethod,
      isLocationBased: d.isLocationBased ? 1 : 0,
      isProductBased: d.isProductBased ? 1 : 0,
      productName: d.productName ?? prev.productName,
    }));
  }, [sourceDetailsData]);

  // Pre-fill step-3 call flow from source
  useEffect(() => {
    if (!sourceCallFlowData?.data?.callFlow?.length) return;
    const callFlow = sourceCallFlowData.data.callFlow.map((item: any) => ({
      id: crypto.randomUUID(),
      // ObjectionList reads both `objection` and `question`; supply both for compatibility
      objection: item.question,
      question: item.question,
      category: item.category,
      subtext: item.subtext ?? "",
      imageURL: item.imageURL ?? null,
      imageName: item.imageName ?? null,
    }));
    setForm((prev: any) => ({ ...prev, callFlow }));
  }, [sourceCallFlowData]);

  // Sales aid files for the details form
  const { data: salesAidData } = useQuery({
    queryKey: ["sales-aid-certification-files"],
    queryFn: () => api.getSalesAidCertificationFiles(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });
  const salesAidFiles = salesAidData?.data || [];
  const salesAidFileOptions = useMemo(
    () => salesAidFiles.map((file) => ({ label: file.file_name, value: file.id, code: file.code })),
    [salesAidFiles]
  );

  // Populate the document code field once salesAidFiles resolves
  useEffect(() => {
    if (!form.salesAidFile || !salesAidFiles.length) return;
    const match = salesAidFiles.find((f: any) => String(f.id) === String(form.salesAidFile));
    if (match && form.code !== match.code) {
      setForm((prev: any) => ({ ...prev, code: match.code }));
    }
  }, [form.salesAidFile, salesAidFiles]);

  // Categories keyed by document so they update immediately when the user changes the sales aid file
  const { data: categoriesData } = useQuery({
    queryKey: ["certification-categories", form.salesAidFile],
    queryFn: () => api.getCertificationCategoriesByDocument(form.salesAidFile),
    enabled: !!form.salesAidFile,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });
  const categories = categoriesData?.data || [];

  // Used to pass the step-1 payload into the duplicate→update chain without
  // relying on potentially-stale closure state inside mutation callbacks.
  const pendingMetadataPayload = useRef<any>(null);

  // Step 1 — first leg: POST /duplicate to create the shell
  const { mutate: duplicateCertification, isLoading: isLoadingDuplicate } = useMutation({
    mutationFn: () => api.duplicateCertification(sourceCertificationId),
    onSuccess: (data: any) => {
      const newId = data?.data?.certificationID;
      if (!newId) {
        ShowToast({ message: `Error duplicating ${sessionSingular.toLowerCase()}`, type: "error" });
        return;
      }
      setForm((prev: any) => ({ ...prev, certificationId: newId }));
      updateCertification({ certificationId: newId, data: pendingMetadataPayload.current });
    },
    onError: () => {
      ShowToast({ message: `Failed to duplicate ${sessionSingular.toLowerCase()}`, type: "error" });
    },
  });

  // Step 1 — second leg: PATCH /metadata with user's edits
  const { mutate: updateCertification, isLoading: isLoadingUpdate } = useMutation({
    mutationFn: (args: { certificationId: string; data: any }) =>
      api.updateCertification(args.certificationId, args.data),
    onSuccess: () => {
      setStep(2);
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || `Failed to update ${sessionSingular.toLowerCase()} details`;
      ShowToast({ message: msg, type: "error" });
    },
  });

  // Step 2 — assign users
  const { mutate: assignUsers, isLoading: isLoadingAssign } = useMutation({
    mutationFn: (args: { certificationId: string; users: string[] }) =>
      api.patchCertificationAssignments(args.certificationId, { users: args.users }),
    onSuccess: () => {
      ShowToast({ message: "User assignments saved", type: "success" });
      setStep(3);
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || "Failed to save user assignments";
      ShowToast({ message: msg, type: "error" });
    },
  });

  // Step 3 — save call flow
  const { mutate: saveCallFlow, isLoading: isLoadingSaveCallFlow } = useMutation({
    mutationFn: (args: { certificationId: string; data: any }) =>
      api.saveCallFlow(args.certificationId, args.data),
    onSuccess: () => {
      ShowToast({ message: `${sessionSingular} duplicated successfully`, type: "success" });
      onClose();
      onSuccess();
    },
    onError: () => {
      ShowToast({ message: "Error saving call flow", type: "error" });
    },
  });

  const handleContinue = () => {
    if (step === 1) {
      const dueDateError = getCertificationDueDateValidationError(
        form.certificationStartDate,
        form.certificationDueDate
      );
      if (dueDateError) {
        setErrorMessage(dueDateError);
        return;
      }
      setErrorMessage(null);
      pendingMetadataPayload.current = buildCertificationMetadataPayload(form, userInfo);
      if (form.certificationId) {
        // Shell already created (user navigated back from step 2) — skip POST /duplicate
        // and go straight to PATCH /metadata with the current form values.
        updateCertification({ certificationId: form.certificationId, data: pendingMetadataPayload.current });
      } else {
        duplicateCertification();
      }
    } else if (step === 2) {
      const users = (form.userList || []).map((u: { value: string }) => u.value);
      assignUsers({ certificationId: form.certificationId, users });
    } else if (step === 3) {
      saveCallFlow({ certificationId: form.certificationId, data: buildCallFlowSavePayload(form.callFlow) });
    }
  };

  const isCallFlowValid = useMemo(() => {
    if (!form.callFlow?.length) return false;
    return form.callFlow.every((item: any) => {
      const question = (item.objection ?? item.question ?? "").toString().trim();
      const category = (item.category ?? "").toString().trim();
      return question.length > 0 && category.length > 0;
    });
  }, [form.callFlow]);

  const isDisabled = useMemo(() => {
    if (step === 2) return !form.userList?.length;
    if (step === 3) return !isCallFlowValid;
    const invalidAttempts =
      showOverallSummary &&
      (!form.certificationAttemptsRequired ||
        !form.certificationAttemptsAllowed ||
        Number(form.certificationAttemptsRequired) > Number(form.certificationAttemptsAllowed));
    return (
      !form.certificationName ||
      !form.certificationStartDate ||
      !form.certificationDueDate ||
      !form.certificationAttemptsAllowed ||
      !form.productName ||
      !form.salesAidFile ||
      !form.primer ||
      !!invalidAttempts
    );
  }, [form, step, isCallFlowValid, showOverallSummary]);

  const loading = isLoadingDuplicate || isLoadingUpdate || isLoadingAssign || isLoadingSaveCallFlow;
  const isPreFilling = isOpen && step === 1 && (isLoadingDetails || isLoadingCallFlow);

  return (
    <SideModal
      show={isOpen}
      toggle={onClose}
      title={`Duplicate ${sessionSingular}`}
      containerClass="flex flex-col"
    >
      <div className="mb-4">
        <div className="text-text-primary text-[15px] mt-1">
          You are making a copy of an existing {sessionSingular.toLowerCase()}.
        </div>
      </div>
      <div className="w-full mb-5">
        <CertificationSteps currentStep={step} isEdit={false} />
      </div>
      <div className="flex flex-col gap-4 max-h-[75vh] overflow-auto">
        {isPreFilling ? (
          <div className="flex items-center justify-center h-40">
            <div className="text-gray-500 text-sm">Loading {sessionSingular.toLowerCase()} data…</div>
          </div>
        ) : (
          <>
            {step === 1 && (
              <CertificationFormBasicFields
                form={form}
                setForm={setForm}
                salesAidFiles={salesAidFiles}
                salesAidFileOptions={salesAidFileOptions}
              />
            )}
            {step === 2 && <CertificationAssignUserStep form={form} setForm={setForm} />}
            {step === 3 && (
              <CertificationFormQuestions form={form} setForm={setForm} categories={categories} />
            )}
          </>
        )}
      </div>
      {!!errorMessage && (
        <div className="flex items-center gap-2 px-2.5 py-2 mb-2 bg-red-50 rounded-[10px]">
          <ExclamationCircleIcon className="w-6 h-6" />
          <span className="text-black text-base">{errorMessage}</span>
        </div>
      )}
      <div
        className={twMerge(
          "flex justify-between w-full flex-1 items-center",
          step === 1 ? "!justify-end" : ""
        )}
      >
        {step !== 1 && (
          <Button
            variant="primary"
            className="!bg-[#D2D2D2] !h-[37px] w-[117px] !text-lg !font-normal !text-black !rounded-full hover:opacity-80 flex items-center gap-1"
            disabled={loading}
            onClick={() => { setStep((prev) => prev - 1); setErrorMessage(null); }}
          >
            Back
          </Button>
        )}
        <Button
          onClick={handleContinue}
          variant="primary"
          className="h-[37px] !w-auto !px-3 !text-lg !font-normal !rounded-full"
          loading={loading}
          disabled={isDisabled || loading || isPreFilling}
        >
          {step === 3 ? `Duplicate ${sessionSingular}` : "Continue"}
        </Button>
      </div>
    </SideModal>
  );
};
